/**
 * EvalGroupPage (`/evals/groups/:slug`) — one eval group.
 *
 * The improvement-over-time step chart (group score as of each run date,
 * narrowable to one task or one variant) sits above the task × variant
 * score matrix (rows link to task pages,
 * cells to runs, hover shows each cell's trend). Matrix column headers add
 * `?variant=` so the same page shows one variant compared across every
 * task (one climb line per task).
 */

import { Navigate, Link, useParams, useSearchParams } from 'react-router-dom'
import { getApiErrorDetail, getApiErrorStatus } from '@/api/client'

import { ConfusionMatrix } from '@/components/evals/ConfusionMatrix'
import { isConfusionGroup } from '@/components/evals/confusion'
import { DraftMarker, NewMarker } from '@/components/evals/DraftMarker'
import { isNewTask } from '@/components/evals/newTask'
import { EvalBreadcrumb } from '@/components/evals/EvalBreadcrumb'
import { formatScore, formatVariant, type ScoreFormat } from '@/components/evals/format'
import {
  groupHistoryCaption,
  groupScoreDescription,
  groupVariantScope,
} from '@/components/evals/groupScore'
import { ScoreHistoryChart, VariantHistoryChart } from '@/components/evals/ScoreHistoryChart'
import { StatRow, type Stat } from '@/components/evals/StatRow'
import { selectedTaskVariant } from '@/components/evals/taskVariant'
import { VariantMatrix } from '@/components/evals/VariantMatrix'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { useTeam } from '@/contexts/TeamContext'
import { useEvalGroup, useRenameEvalGroup, useSetGroupSync } from '@/hooks/useEvals'
import { RenameDialog } from '@/components/shared/RenameDialog'
import type { ScoreSeries } from '@/components/evals/ScoreHistoryChart'
import type { EvalTaskSummary } from '@/api/generated/model'

/** One series per task that has runs for the selected variant. */
function variantAcrossTasks(tasks: EvalTaskSummary[], variant: string): ScoreSeries[] {
  return tasks.flatMap((task) => {
    const cell = task.variant_scores.find((entry) => entry.variant === variant)
    // Reference-only cells (score null, empty history) have no line to draw.
    return cell && cell.history.length > 0
      ? [{ variant: task.display_name, points: cell.history }]
      : []
  })
}

/** Headline numbers for one variant across the group — task-page-style stats. */
function variantStats(tasks: EvalTaskSummary[], variant: string, format: ScoreFormat): Stat[] {
  const cells = tasks.flatMap((task) =>
    task.variant_scores.filter((entry) => entry.variant === variant)
  )
  // Reference-only cells carry no scored datapoint — every stat in this row
  // reads over the SAME basis: cells with a scored run.
  const scored = cells.filter((cell) => cell.score !== null)
  const scores = scored.flatMap((cell) => (cell.score === null ? [] : [cell.score]))
  if (scores.length === 0) return []
  const runCount = scored.reduce((sum, cell) => sum + cell.history.length, 0)
  const meanScore = scores.reduce((sum, score) => sum + score, 0) / scores.length
  const lastRunMs = Math.max(
    ...scored.flatMap((cell) => cell.history.map((point) => new Date(point.date).getTime()))
  )
  return [
    { label: 'Mean score', value: formatScore(meanScore, format) },
    { label: 'Tasks', value: scored.length.toString() },
    { label: 'Runs', value: runCount.toString() },
    {
      label: 'Last run',
      value: new Date(lastRunMs).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    },
  ]
}

/**
 * Task list for a group whose tasks are synced but unrun — VariantMatrix
 * has no run to build columns from, so this stands in until the first push.
 */
function SyncedTaskList({
  groupSlug,
  tasks,
}: {
  groupSlug: string
  tasks: EvalTaskSummary[]
}): React.JSX.Element {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Synced tasks awaiting first run
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-x-3 gap-y-1" style={{ gridTemplateColumns: 'minmax(0, max-content) minmax(0, 1fr)' }}>
          {tasks.map((task) => (
            <div key={task.id} className="contents">
              <span className="flex min-w-0 items-center gap-2">
                <Link
                  to={`/evals/groups/${groupSlug}/tasks/${task.slug}`}
                  className={`truncate text-sm hover:text-primary hover:underline ${
                    task.status === 'draft' ? 'text-muted-foreground' : 'text-foreground'
                  }`}
                >
                  {task.display_name}
                </Link>
                {task.status === 'draft' && <DraftMarker prUrl={task.pr_url} />}
                {task.status === 'active' && isNewTask(task.created_at) && <NewMarker />}
              </span>
              <span
                aria-label={
                  task.status === 'draft'
                    ? `${task.display_name}: draft pending PR merge`
                    : `${task.display_name}: no runs yet`
                }
                className="h-8 rounded border border-dashed border-border"
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

/** Header pencil (TRA-947); the optimistic hook updates the /evals card too. */
function GroupRenameButton({ slug, displayName }: { slug: string; displayName: string }): React.JSX.Element {
  const { rename } = useRenameEvalGroup(slug)
  return (
    <RenameDialog
      title="Rename eval group"
      slug={slug}
      displayName={displayName}
      maxLength={120}
      onSave={rename}
    />
  )
}

/**
 * Auto-sync switch for a repo-synced group. Turning it off keeps the
 * nightly benchmark inventory from touching this group, so new repo tests
 * do not appear here until it is back on.
 */
function SyncToggle({ slug, enabled }: { slug: string; enabled: boolean }): React.JSX.Element | null {
  const { isRestricted } = useTeam()
  const { setEnabled, isPending } = useSetGroupSync(slug)
  // No sync toggling on restricted demo teams (TRA-832)
  if (isRestricted) {
    return null
  }
  return (
    <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
      Auto-sync from GitHub
      <Switch
        checked={enabled}
        disabled={isPending}
        onCheckedChange={(next) => {
          setEnabled(next)
        }}
        aria-label="Auto-sync this group from GitHub"
      />
    </label>
  )
}

export default function EvalGroupPage(): React.JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const variant = selectedTaskVariant(searchParams)
  const { data: group, isLoading, isError, error } = useEvalGroup(slug)

  return (
    <PageContainer className="space-y-6">
      <EvalBreadcrumb
        crumbs={[
          { label: 'Eval Groups', to: '/evals' },
          { label: group?.display_name ?? slug ?? '' },
        ]}
      />

      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-60 w-full" />
        </div>
      )}

      {/* A 404 means the resource is not in the active team (team switch) or was deleted: go to the section root, not a dead error page. */}
      {isError && getApiErrorStatus(error) === 404 && <Navigate to="/evals" replace />}
      {isError && getApiErrorStatus(error) !== 404 && (
        <p className="text-sm text-destructive">
          Failed to load eval group: {getApiErrorDetail(error) ?? 'unknown error'}
        </p>
      )}

      {!isLoading && !isError && group && (
        <>
          <PageHeader
            className="group/rename"
            title={variant === null ? group.display_name : `${group.display_name} · ${formatVariant(variant)}`}
            description={
              variant === null
                ? groupScoreDescription(group.score, group.splits, group.score_format)
                : `The ${formatVariant(variant)} variant compared across every task in the group.`
            }
            actions={
              <div className="flex items-center gap-4">
                <GroupRenameButton slug={group.slug} displayName={group.display_name} />
                {group.source.startsWith('github:') && (
                  <SyncToggle slug={group.slug} enabled={group.sync_enabled} />
                )}
                {variant === null ? (
                  <Button asChild variant="outline">
                    <Link to={`/evals/groups/${group.slug}/analysis`}>Analysis</Link>
                  </Button>
                ) : (
                  <Button asChild variant="outline">
                    <Link to={`/evals/groups/${group.slug}`}>All variants</Link>
                  </Button>
                )}
              </div>
            }
          />

          {/* A variant can exist as reference runs only (a level to beat with
              no climb yet) — its column still links here, so this page needs
              a state for it instead of rendering nothing. */}
          {variant !== null && variantStats(group.tasks, variant, group.score_format).length === 0 && (
            <p className="text-sm text-muted-foreground">
              This measurement has no scored runs yet. Pick a reference in the
              score table below to see its results.
            </p>
          )}

          {variant !== null && variantStats(group.tasks, variant, group.score_format).length > 0 && (
            <StatRow stats={variantStats(group.tasks, variant, group.score_format)} />
          )}

          {variant !== null && (
            <VariantHistoryChart
              title={`${formatVariant(variant)} across tasks over time`}
              series={variantAcrossTasks(group.tasks, variant)}
              format={group.score_format}
            />
          )}

          {variant === null && (
            <ScoreHistoryChart
              title="Group score over time"
              points={group.history}
              caption={groupHistoryCaption(group.splits)}
              variantScope={groupVariantScope(group.splits)}
              format={group.score_format}
              referenceLines={group.reference_lines}
              tasks={group.task_history}
              variants={group.variant_history}
            />
          )}

          {group.tasks.length === 0 ? (
            <EmptyState
              title="No tasks in this group yet"
              description="Tasks will appear here once they're synced or pushed."
            />
          ) : group.tasks.every((task) => task.variant_scores.length === 0) ? (
            <SyncedTaskList groupSlug={group.slug} tasks={group.tasks} />
          ) : isConfusionGroup(group.tasks) ? (
            <ConfusionMatrix groupSlug={group.slug} tasks={group.tasks} />
          ) : (
            <VariantMatrix
              groupSlug={group.slug}
              tasks={group.tasks}
              variant={variant ?? undefined}
              format={group.score_format}
            />
          )}
        </>
      )}
    </PageContainer>
  )
}
